import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";
import { set } from "date-fns";

// Create the CandidateContext
const EmployeeDetailsContext = createContext();

// Custom hook to use the CandidateContext
export const useEmployeeDetails = () => useContext(EmployeeDetailsContext);

// Provider component
export const EmployeeDetailsProvider = ({ children }) => {
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const API_URL = "https://hrmsnew.prepstripe.com/api/v1/employee-details"; // Replace with your actual API URL
  const token = getToken(); // Retrieve the token
  const [error, setError] = useState(null);

  // Fetch candidates from the API on load
  const fetchEmployeeDetails = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await new Promise((r) => setTimeout(r, 1000));

      setEmployeeDetails(response.data?.data?.data || []);
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 20,
      });
      setError(null);
      setErrors({});
    } catch (error) {
      console.error("Error fetching employee details:", error);
      const errData = error.response?.data || {};
      const generalError =
        errData.message || "Failed to fetch employee details.";
      setError(generalError);
      setErrors(errData.errors || {});
    } finally {
      setLoading(false);
    }
  };

  // Use the fetch function in useEffect
  useEffect(() => {
    fetchEmployeeDetails();
  }, [API_URL, token]);

  const addEmployeeDetails = async (newEmployeeDetail) => {
    try {
      setLoading(true);
      const response = await axios.post(API_URL, newEmployeeDetail, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setEmployeeDetails((prev) => [...prev, response.data?.data]);
      setEmployeeDetails((prev) => [response.data?.data, ...prev]);

      setLoading(false);
      return { success: true };
    } catch (error) {
      setLoading(false);
      console.error("Error adding employee:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add employee.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Update an existing candidate
  // const updateEmployeeDetails = async (updatedEmployeeDetail) => {
  //   try {
  //     // // console.log("updatedEmployeeDetail", updatedEmployeeDetail);
  //     setLoading(true);
  //     const isFormData = updatedEmployeeDetail instanceof FormData;
  //     // console.log("is formdata:", isFormData)
  //     const headers = {
  //       Authorization: `Bearer ${token}`,
  //       ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
  //     };

  //     // console.log(`${API_URL}/${updatedEmployeeDetail.employee_id}`)
  //     // console.log("about to send the payload", updatedEmployeeDetail)
  //     const response = await axios.put(`${API_URL}/${updatedEmployeeDetail.employee_id}`, updatedEmployeeDetail, {
  //       headers,
  //     });

  //     // console.log(response.data)

  //     setEmployeeDetails((prevRequests) =>
  //       prevRequests.map((request) =>
  //         request.id === updatedEmployeeDetail.id ? { ...request, ...updatedEmployeeDetail } : request
  //       )
  //     );
  //     setLoading(false);
  //     setErrors({}); // Clear errors on successful update
  //     return response.data?.data; // Optionally return updated data
  //   } catch (error) {
  //     setLoading(false);
  //     console.error("Error updating employee details:", error);
  //     const generalError = error.response?.data?.message || "Something went wrong.";
  //     setErrors({ general: generalError, ...(error.response?.data?.errors || {}) });
  //     throw error; // Optionally rethrow for higher-level handling
  //   }
  // };

  // const updateEmployeeDetails = async (updatedEmployeeDetail) => {
  //   try {
  //     setLoading(true);
  //     const isFormData = updatedEmployeeDetail instanceof FormData;
  //     // console.log("is formdata:", isFormData);

  //     // Only set Authorization header, let axios handle Content-Type for FormData
  //     const headers = {
  //       Authorization: `Bearer ${token}`
  //     };

  //     // Get the employee_id from FormData for the URL
  //     const employeeId = isFormData
  //       ? updatedEmployeeDetail.get('employee_id')
  //       : updatedEmployeeDetail.employee_id;

  //     // console.log(`${API_URL}/${employeeId}`);
  //     // console.log("about to send the payload", updatedEmployeeDetail);

  //     const response = await axios.put(`${API_URL}/${employeeId}`, updatedEmployeeDetail, {
  //       headers
  //     });

  //     // console.log(response.data);

  //     // Update the local state - you might need to adjust this part
  //     // based on what data you get back from the server
  //     setEmployeeDetails((prevRequests) =>
  //       prevRequests.map((request) =>
  //         request.id === employeeId ? { ...request, ...response.data?.data } : request
  //       )
  //     );

  //     setLoading(false);
  //     setErrors({}); // Clear errors on successful update
  //     return response.data?.data; // Optionally return updated data
  //   } catch (error) {
  //     setLoading(false);
  //     console.error("Error updating employee details:", error);
  //     const generalError = error.response?.data?.message || "Something went wrong.";
  //     setErrors({ general: generalError, ...(error.response?.data?.errors || {}) });
  //     throw error; // Optionally rethrow for higher-level handling
  //   }
  // };

  // const updateEmployeeDetails = async (updatedEmployeeDetail) => {
  //   try {
  //     setLoading(true);
  //     const isFormData = updatedEmployeeDetail instanceof FormData;
  //     // console.log("is formdata:", isFormData);

  //     // Only set Authorization header
  //     const headers = {
  //       Authorization: `Bearer ${token}`
  //     };

  //     // Get the employee_id, handling potential null value
  //     let employeeId;
  //     if (isFormData) {
  //       employeeId = updatedEmployeeDetail.get('employee_id');
  //       if (employeeId === null || employeeId === 'null' || employeeId === undefined) {
  //         throw new Error("Employee ID is required but was null or undefined");
  //       }
  //     } else {
  //       employeeId = updatedEmployeeDetail.employee_id;
  //       if (employeeId === null || employeeId === undefined) {
  //         throw new Error("Employee ID is required but was null or undefined");
  //       }
  //     }

  //     // console.log(`${API_URL}/${employeeId}`);
  //     // console.log("about to send the payload", updatedEmployeeDetail);

  //     const response = await axios.put(`${API_URL}/${employeeId}`, updatedEmployeeDetail, {
  //       headers
  //     });

  //     // console.log(response.data);

  //     // Update state using response data
  //     setEmployeeDetails((prevRequests) =>
  //       prevRequests.map((request) =>
  //         request.id === employeeId ? { ...request, ...response.data?.data } : request
  //       )
  //     );

  //     setLoading(false);
  //     setErrors({});
  //     return response.data?.data;
  //   } catch (error) {
  //     setLoading(false);
  //     console.error("Error updating employee details:", error);
  //     const generalError = error.response?.data?.message || "Something went wrong.";
  //     setErrors({ general: generalError, ...(error.response?.data?.errors || {}) });
  //     throw error;
  //   }
  // };

  const updateEmployeeDetails = async (formData) => {
    try {
      setLoading(true);

      // Ensure formData is actually FormData
      if (!(formData instanceof FormData)) {
        throw new Error(
          "Expected FormData but received a different object type",
        );
      }

      // Get employee_id from FormData
      const employeeId = formData.get("employee_id");

      // console.log("employee id: ", employeeId)
      // console.log("employee id in number: ", Number(employeeId))

      // Only set Authorization header - don't set Content-Type for FormData
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.post(`${API_URL}/${employeeId}`, formData, {
        headers,
      });

      // Rest of your function...
      setEmployeeDetails((prevRequests) =>
        prevRequests.map((request) =>
          request.employee_id === formData.get("employee_id")
            ? { ...request, ...response.data?.data }
            : request,
        ),
      );

      setLoading(false);
      setErrors({});
      setError(null);
      // return response.data?.data;
      return { success: true };
    } catch (error) {
      const errData = error.response?.data || {};
      const generalError = errData.message || "Something went wrong.";

      const allErrors = {
        general: generalError,
        ...(errData.errors || {}),
      };

      setErrors(allErrors);
      setError(generalError);
      return {
        success: false,
        message: generalError,
        errors: errData.errors || {},
      };
    } finally {
      setLoading(false);
    }
  };

  // Delete a Employee details
  const deleteEmployeeDetails = async (id) => {
    try {
      setLoading(true);
      // console.log("Deleting employee details with ID:", id);
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmployeeDetails((prev) =>
        prev.filter((employeeDetail) => employeeDetail.employee_id !== id),
      );
      setLoading(false);
      return { success: true };
    } catch (error) {
      setLoading(false);
      console.error(
        "Error deleting employee details:",
        error.response?.data?.message,
      );
      setError(
        error.response?.data?.message || "Failed to delete employee details.",
      );
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      employeeDetails,
      pagination,
      loading,
      error,
      fetchEmployeeDetails,
      addEmployeeDetails,
      updateEmployeeDetails,
      deleteEmployeeDetails,
      errors,
    }),
    [employeeDetails, errors, loading], // Recalculate the value only when these dependencies change
  );

  return (
    <EmployeeDetailsContext.Provider value={value}>
      {children}
    </EmployeeDetailsContext.Provider>
  );
};
